import React, { useState } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Box,
  Stack,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  InputAdornment,
  Snackbar,
  Fade,
  Slide,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import {
  ArrowLeft,
  CheckCircle,
  X,
  Truck,
  Package,
  User,
  MapPin,
  DollarSign,
  Check,
  XCircle,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import { API_ENDPOINT } from "../../constants/constants";
import { formatAmount, getHeader } from "../../utils/util";
import { LoadingButton } from "@mui/lab";

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const ToolsOrderDetails = () => {
  const { id: order_id } = useParams();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, action: "" });
  const [paymentDialog, setPaymentDialog] = useState({ open: false });
  const [newPaymentStatus, setNewPaymentStatus] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [shippingError, setShippingError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
    icon: null,
  });
  const [imageDialog, setImageDialog] = useState({
    open: false,
    imageUrl: "",
    zoom: 1,
    rotation: 0,
  });

  const {
    data: order,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`admin/get-tools-order/${order_id}`);

  const showNotification = (message, type = "success") => {
    setNotification({
      open: true,
      message,
      type,
      icon: type === "success" ? <Check size={24} /> : <XCircle size={24} />,
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleImageOpen = (imageUrl) => {
    setImageDialog({
      open: true,
      imageUrl,
      zoom: 1,
      rotation: 0,
    });
  };

  const handleImageClose = () => {
    setImageDialog({
      open: false,
      imageUrl: "",
      zoom: 1,
      rotation: 0,
    });
  };

  const handleZoomIn = () => {
    setImageDialog((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 0.25, 3),
    }));
  };

  const handleZoomOut = () => {
    setImageDialog((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 0.25, 0.5),
    }));
  };

  const handleRotate = () => {
    setImageDialog((prev) => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const handleResetImage = () => {
    setImageDialog((prev) => ({
      ...prev,
      zoom: 1,
      rotation: 0,
    }));
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      let successMessage = "";
      setIsUpdating(true);

      if (
        actionDialog.action === "accept" ||
        actionDialog.action === "reject"
      ) {
        let requestBody = { status: newStatus };

        if (actionDialog.action === "accept") {
          const shipping = parseFloat(shippingCost);

          if (!shippingCost || isNaN(shipping) || shipping < 0) {
            setShippingError("Please enter a valid shipping cost");
            setIsUpdating(false);
            return;
          }

          requestBody.shipping = shipping;
          successMessage =
            "Order accepted successfully! Shipping cost has been added.";
        } else {
          successMessage = "Order has been rejected successfully.";
        }

        const response = await fetch(
          `${API_ENDPOINT}admin/accept-reject-order/${order_id}`,
          {
            method: "POST",
            ...getHeader(),
            body: JSON.stringify(requestBody),
          }
        );

        if (response.ok) {
          setActionDialog({ open: false, action: "" });
          setShippingCost("");
          setShippingError("");
          showNotification(successMessage, "success");
          await refetch();
        } else {
          showNotification(
            "Failed to update order status. Please try again.",
            "error"
          );
        }
      } else {
        const response = await fetch(
          `${API_ENDPOINT}admin/update-order-status/${order_id}`,
          {
            method: "PATCH",
            ...getHeader(),
            body: JSON.stringify({ status: newStatus }),
          }
        );

        if (response.ok) {
          setActionDialog({ open: false, action: "" });
          showNotification(
            `Order status updated to ${newStatus} successfully!`,
            "success"
          );
          await refetch();
        } else {
          showNotification(
            "Failed to update order status. Please try again.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Failed to update order status:", error);
      showNotification("An error occurred. Please try again.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePaymentUpdate = async () => {
    if (!newPaymentStatus) {
      showNotification("Please select a payment status.", "error");
      return;
    }

    if (newPaymentStatus === "paid") {
      if (!transactionId.trim()) {
        showNotification("Please enter a transaction ID.", "error");
        return;
      }
      if (!paymentDate) {
        showNotification("Please enter a payment date.", "error");
        return;
      }
    }

    try {
      setIsUpdating(true);

      let requestBody = { status: newPaymentStatus };
      if (newPaymentStatus === "paid") {
        requestBody.transactionId = transactionId.trim();
        requestBody.paymentDate = paymentDate;
      }

      const response = await fetch(
        `${API_ENDPOINT}admin/update-order-payment-status/${order_id}`,
        {
          method: "POST",
          ...getHeader(),
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        setPaymentDialog({ open: false });
        setNewPaymentStatus("");
        setTransactionId("");
        setPaymentDate("");
        showNotification(
          `Payment status updated to ${newPaymentStatus} successfully!`,
          "success"
        );
        await refetch();
      } else {
        showNotification(
          "Failed to update payment status. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Failed to update payment status:", error);
      showNotification("An error occurred. Please try again.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      accepted: "success",
      shipped: "info",
      delivered: "primary",
      cancelled: "error",
      rejected: "error",
    };
    return colors[status] || "default";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: "warning",
      processing: "info",
      rejected: "error",
      paid: "success",
    };
    return colors[status] || "default";
  };

  const getActionButtons = () => {
    switch (order.orderStatus) {
      case "pending":
        return (
          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle size={20} />}
              onClick={() => setActionDialog({ open: true, action: "accept" })}
            >
              Accept Order
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<X size={20} />}
              onClick={() => setActionDialog({ open: true, action: "reject" })}
            >
              Reject Order
            </Button>
          </Box>
        );
      case "accepted":
        return (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="info"
              startIcon={<Truck size={20} />}
              onClick={() => setActionDialog({ open: true, action: "ship" })}
            >
              Ship Order
            </Button>
          </Box>
        );
      case "shipped":
        return (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle size={20} />}
              onClick={() => setActionDialog({ open: true, action: "deliver" })}
            >
              Mark as Delivered
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  const getDialogConfig = (action) => {
    const config = {
      title: "",
      message: "",
      newStatus: "",
      confirmColor: "primary",
      showWarning: false,
      showShippingInput: false,
    };

    switch (action) {
      case "accept":
        config.title = "Accept Order";
        config.message = "Please enter the shipping cost for this order.";
        config.newStatus = "accepted";
        config.confirmColor = "success";
        config.showShippingInput = true;
        break;
      case "reject":
        config.title = "Reject Order";
        config.message = "Are you sure you want to reject this order?";
        config.newStatus = "rejected";
        config.confirmColor = "error";
        config.showWarning = true;
        break;
      case "ship":
        config.title = "Ship Order";
        config.message = "Are you sure you want to mark this order as shipped?";
        config.newStatus = "shipped";
        config.confirmColor = "info";
        break;
      case "deliver":
        config.title = "Mark as Delivered";
        config.message =
          "Are you sure you want to mark this order as delivered?";
        config.newStatus = "delivered";
        config.confirmColor = "success";
        break;
      default:
        break;
    }

    return config;
  };

  const handleDialogClose = () => {
    setActionDialog({ open: false, action: "" });
    setShippingCost("");
    setShippingError("");
  };

  const handlePaymentDialogOpen = () => {
    setNewPaymentStatus(order.paymentStatus || "");
    setTransactionId("");
    setPaymentDate("");
    setPaymentDialog({ open: true });
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialog({ open: false });
    setNewPaymentStatus("");
    setTransactionId("");
    setPaymentDate("");
  };

  if (isLoading)
    return <Container sx={{ mt: 4 }}>Loading order details...</Container>;
  if (isError)
    return <Container sx={{ mt: 4 }}>Error: {error?.message}</Container>;
  if (!order) return <Container sx={{ mt: 4 }}>Order not found</Container>;

  const dialogConfig = getDialogConfig(actionDialog.action);

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Orders
        </Button>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h1" gutterBottom>
              Order #{order.orderId}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Placed on {new Date(order.createdOn).toLocaleDateString()}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { md: "flex-end" },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1, justifyContent: { md: "flex-end" } }}
            >
              <Chip
                label={order.orderStatus.toUpperCase()}
                color={getStatusColor(order.orderStatus)}
                size="large"
                sx={{ fontSize: "1rem", px: 2 }}
              />
              <Chip
                label={
                  order.paymentStatus
                    ? order.paymentStatus.toUpperCase()
                    : "PENDING"
                }
                color={getPaymentStatusColor(order.paymentStatus || "pending")}
                size="large"
                sx={{ fontSize: "1rem", px: 2 }}
              />
            </Stack>
            {getActionButtons()}
            {order.receiptUrl && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ mt: 1 }}
                onClick={handlePaymentDialogOpen}
              >
                Update Payment Status
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Order Items */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Package size={20} /> Order Items
              </Typography>
              <Stack spacing={2}>
                {order.items.map((item, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        {item.images && item.images[0] && (
                          <Box
                            sx={{
                              position: "relative",
                              cursor: "pointer",
                              "&:hover .image-overlay": {
                                opacity: 1,
                              },
                            }}
                            onClick={() => handleImageOpen(item.images[0])}
                          >
                            <img
                              src={item.images[0]}
                              alt={item.name}
                              style={{
                                width: "100%",
                                height: 100,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                            <Box
                              className="image-overlay"
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(0,0,0,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 8,
                                opacity: 0,
                                transition: "opacity 0.2s",
                              }}
                            >
                              <ZoomIn color="white" size={24} />
                            </Box>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6">{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Quantity: {item.quantity}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          GST %: {item.gst}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Price: ₹{item.price} each
                        </Typography>
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: "right" }}>
                        <Typography variant="h6" color="primary">
                          ₹{item.itemTotal}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Summary & Customer Info */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Customer Information */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <User size={20} /> Customer Information
                </Typography>
                <Stack spacing={1}>
                  <Typography>
                    <strong>Name:</strong> {order.shippingAddress.fullName}
                  </Typography>
                  <Typography>
                    <strong>Phone:</strong> {order.shippingAddress.phone}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong>{" "}
                    {order.shippingAddress.email || "N/A"}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <MapPin size={20} /> Shipping Address
                </Typography>
                <Typography variant="body2">
                  {order.shippingAddress.address}
                  <br />
                  {order.shippingAddress.city}, {order.shippingAddress.state}
                  <br />
                  {order.shippingAddress.zipCode}
                  <br />
                  {order.shippingAddress.country}
                </Typography>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DollarSign size={20} /> Order Summary
                </Typography>
                <Stack spacing={1}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>Subtotal:</Typography>
                    <Typography>
                      ₹{formatAmount(order.orderSummary.subtotal)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>GST:</Typography>
                    <Typography>
                      ₹{formatAmount(order.orderSummary.gst)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>Shipping:</Typography>
                    <Typography>
                      ₹{formatAmount(order.orderSummary.shipping || 0)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      ₹
                      {formatAmount(
                        order.orderSummary.subtotal +
                          order.orderSummary.gst +
                          (order.orderSummary.shipping || 0)
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Payment Receipt */}
            {order.receiptUrl && (
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <DollarSign size={20} /> Payment Receipt
                  </Typography>
                  <Box
                    sx={{
                      textAlign: "center",
                      position: "relative",
                      cursor: "pointer",
                      "&:hover .image-overlay": {
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleImageOpen(order.receiptUrl)}
                  >
                    <img
                      src={order.receiptUrl}
                      alt="Payment Receipt"
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                        maxHeight: 300,
                        borderRadius: 8,
                        boxShadow: 1,
                        objectFit: "contain",
                      }}
                    />
                    <Box
                      className="image-overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <ZoomIn color="white" size={32} />
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ZoomIn size={18} />}
                    onClick={() => handleImageOpen(order.receiptUrl)}
                    sx={{ mt: 2 }}
                  >
                    View Full Receipt
                  </Button>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Order Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogConfig.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{dialogConfig.message}</Typography>

          {dialogConfig.showShippingInput && (
            <TextField
              fullWidth
              label="Shipping Cost"
              type="number"
              value={shippingCost}
              onChange={(e) => {
                setShippingCost(e.target.value);
                setShippingError("");
              }}
              error={!!shippingError}
              helperText={shippingError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              autoFocus
              sx={{ mt: 1 }}
            />
          )}

          {dialogConfig.showWarning && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. The customer will be notified.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={isUpdating}
            color={dialogConfig.confirmColor}
            onClick={() => handleStatusUpdate(dialogConfig.newStatus)}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Payment Status Update Dialog */}
      <Dialog
        open={paymentDialog.open}
        onClose={handlePaymentDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Select the new payment status for this order:
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={newPaymentStatus}
              label="Payment Status"
              onChange={(e) => setNewPaymentStatus(e.target.value)}
              autoFocus
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </Select>
          </FormControl>
          {newPaymentStatus === "paid" && (
            <>
              <TextField
                fullWidth
                label="Transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                required
                sx={{ mt: 2 }}
              />
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={paymentDate}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
                sx={{ mt: 2 }}
              />
            </>
          )}
          {newPaymentStatus === "rejected" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. The customer will be notified.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={isUpdating}
            color="primary"
            onClick={handlePaymentUpdate}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Full Screen Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={handleImageClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "rgba(0,0,0,0.9)",
            color: "white",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">Image Preview</Typography>
          <IconButton onClick={handleImageClose} sx={{ color: "white" }}>
            <X size={24} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            p: 2,
          }}
        >
          <Box
            sx={{
              transform: `rotate(${imageDialog.rotation}deg)`,
              transition: "transform 0.3s ease",
            }}
          >
            <img
              src={imageDialog.imageUrl}
              alt="Full screen view"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                transform: `scale(${imageDialog.zoom})`,
                transition: "transform 0.2s ease",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 1,
            pb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ZoomOut size={18} />}
            onClick={handleZoomOut}
            disabled={imageDialog.zoom <= 0.5}
          >
            Zoom Out
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetImage}
            startIcon={<RotateCw size={18} />}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<ZoomIn size={18} />}
            onClick={handleZoomIn}
            disabled={imageDialog.zoom >= 3}
          >
            Zoom In
          </Button>
          <Button
            variant="outlined"
            startIcon={<RotateCw size={18} />}
            onClick={handleRotate}
          >
            Rotate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Fade in={notification.open} timeout={500}>
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            variant="filled"
            sx={{
              width: "100%",
              alignItems: "center",
              fontSize: "1rem",
              py: 2,
              boxShadow: 3,
              borderRadius: 2,
            }}
            icon={notification.icon}
          >
            <Typography variant="body1" fontWeight="medium">
              {notification.message}
            </Typography>
          </Alert>
        </Fade>
      </Snackbar>
    </Container>
  );
};

export default ToolsOrderDetails;
